import FullScreenLoading from '@components/playbook/full-screen-loading'
import Header from '@components/playbook/header'
import { PreHeader } from '@components/playbook/pre-header'
import { LoggedOutURLS, OnboardingURLS, URLS } from '@components/playbook/urls'
import { DEFAULT_OG_IMAGE } from '@contentful-components/page-meta/default-meta'
import { ProductVariant } from '@getstep/proto/common'
import { AuthenticationFailureError } from '@getstep/sdk/dist/http/auth/AuthenticationFailureError'
import { notNullish, nullish } from '@getstep/sdk/dist/util/Assert'
import analyticsService from '@lib/analytics/AnalyticsService'
import { googleAdsService } from '@lib/analytics/googleAds'
import * as mixpanel from '@lib/analytics/mixpanel'
import { useEffectAsync } from '@lib/hooks/useEffectAsync'
import { usePlaybook } from '@lib/hooks/usePlaybook'
import { PlaybookContext } from '@lib/store/playbook/context'
import { SessionStore } from '@lib/store/session'
import { SessionContext } from '@lib/store/session/context'
import { useStores } from '@lib/store/useStores'
import { playbookUserHasNotKYCed } from '@lib/utils/kyc'
import { isPrd } from '@shared/util/env'
import { observer } from 'mobx-react-lite'
import Head from 'next/head'
import { usePathname, useSearchParams } from 'next/navigation'
import { useRouter } from 'next/router'
import { generateNextSeo } from 'next-seo/pages'
import { useContext, useEffect, useState } from 'react'
import type { FunctionComponent, ReactNode } from 'react'

import styles from './playbook-page.module.scss'

export const getPlaybookLayout = () => {
    return (children: ReactNode) => {
        return <PlaybookWrapper>{children}</PlaybookWrapper>
    }
}

export const PlaybookWrapper: FunctionComponent<Props> = observer(
    ({ children }) => {
        const { sdk, linkingStore, featureOverrideStore } = useStores()
        const sessionStore = useContext(SessionContext)
        const playbook = usePlaybook()
        const router = useRouter()

        const [session] = useState(
            sessionStore ??
                new SessionStore(sdk, linkingStore, featureOverrideStore),
        )

        const pathName = usePathname()
        const searchParams = useSearchParams()
        const [isLayoutLoading, setIsLayoutLoading] = useState(true)

        useEffect(() => {
            localStorage.setItem('isOnboarding', 'lending')
        }, [])

        useEffectAsync(async () => {
            if (!searchParams) return

            // Set playbook-specific user attributes
            await analyticsService.setUserAttributes({
                productVariant: ProductVariant.META_LENDING,
            })
        }, [searchParams])

        // Handle authentication failures gracefully in playbook
        useEffect(() => {
            const handleUnhandledRejection = (event: PromiseRejectionEvent) => {
                if (event.reason instanceof AuthenticationFailureError) {
                    if (!isPrd()) {
                        console.debug(
                            'Unhandled authentication failure, redirecting to login',
                        )
                    }
                    event.preventDefault() // Prevent the error from appearing in console
                    router.replace(URLS.EXCLUSIVE_OFFER)
                }
            }

            window.addEventListener(
                'unhandledrejection',
                handleUnhandledRejection,
            )
            return () => {
                window.removeEventListener(
                    'unhandledrejection',
                    handleUnhandledRejection,
                )
            }
        }, [router])

        useEffectAsync(async () => {
            if (!session.loaded) {
                playbook.setIsLoading(true)
                try {
                    await session.restore()

                    // Set flow type for analytics tracking
                    if (notNullish(session.authorizedStore)) {
                        const flowType =
                            session.authorizedStore.enrollmentFlowManager
                                .flowType

                        // Set for Mixpanel as super property (auto-included in all events, persists across sessions)
                        mixpanel.registerFlowType(flowType)

                        // Also set in attribution data for any events tracked before super property takes effect
                        analyticsService.setAttributionData({
                            ...analyticsService.getAttributionData(),
                            flowType,
                        })

                        // Set for Google Ads conversions
                        googleAdsService.flowType = flowType

                        // Pass UTM params from attribution data to GoogleAdsService for GA4 conversions
                        const attribution =
                            analyticsService.getAttributionData()
                        googleAdsService.setUtmParams({
                            utm_source: attribution?.utm_source,
                            utm_medium: attribution?.utm_medium,
                            utm_campaign: attribution?.utm_campaign,
                        })
                    }

                    if (nullish(session.authorizedStore)) {
                        if (!isPrd()) {
                            console.debug('user is logged out')
                        }
                        if (
                            nullish(
                                LoggedOutURLS.find((url) => url === pathName),
                            )
                        ) {
                            router.replace(URLS.EXCLUSIVE_OFFER)
                        }
                    } else if (
                        playbookUserHasNotKYCed(session.authorizedStore)
                    ) {
                        // User is logged in but hasn't completed KYC
                        // Calculate where they should be in the enrollment flow
                        const expectedUrl =
                            session.authorizedStore.enrollmentFlowManager.getNextStepUrl()

                        // Compare base paths without query params to preserve verification codes
                        const currentBasePath = pathName.split('?')[0]
                        const expectedBasePath = expectedUrl.split('?')[0]

                        if (currentBasePath !== expectedBasePath) {
                            // User is on wrong step - redirect to correct step
                            if (!isPrd()) {
                                console.debug(
                                    `user is on wrong onboarding step (${pathName}), redirecting to ${expectedUrl}`,
                                )
                            }
                            await router.replace(expectedUrl)
                        } else {
                            // User is on correct step - stay put (preserves query params like ?code=123456)
                            if (!isPrd()) {
                                console.debug(
                                    `user is on correct onboarding step (${pathName}), staying`,
                                )
                            }
                        }
                    } else if (
                        notNullish(
                            OnboardingURLS.find((url) => url === pathName),
                        )
                    ) {
                        if (!isPrd()) {
                            console.debug(
                                'user should not be on pre onboarded screens after onboarding',
                            )
                        }
                        router.replace(URLS.ACCOUNT)
                    }
                } catch (error) {
                    if (error instanceof AuthenticationFailureError) {
                        if (!isPrd()) {
                            console.debug(
                                'Authentication failed, redirecting to login',
                            )
                        }
                        router.replace(URLS.EXCLUSIVE_OFFER)
                    } else {
                        console.error(
                            'Unexpected error during session restore:',
                            error,
                        )
                        router.replace(URLS.EXCLUSIVE_OFFER)
                    }
                } finally {
                    playbook.setIsLoading(false)
                    setIsLayoutLoading(false)
                }
            } else {
                if (!isPrd()) {
                    console.debug('session already loaded')
                }
            }
        }, [])

        if (isLayoutLoading) {
            return (
                <SessionContext.Provider value={session}>
                    <PlaybookContext.Provider value={playbook}>
                        <Head>
                            {generateNextSeo({
                                title: 'Step EarlyPay',
                                description:
                                    'Borrow up to $250 with Step EarlyPay',
                                openGraph: {
                                    title: 'Step EarlyPay',
                                    description:
                                        'Borrow up to $250 with Step EarlyPay',
                                    images: [
                                        {
                                            url: DEFAULT_OG_IMAGE,
                                            width: 1200,
                                            height: 630,
                                            alt: 'Borrow up to $250 with Step EarlyPay',
                                        },
                                    ],
                                },
                                noindex: true,
                                nofollow: true,
                            })}
                        </Head>
                        <div className={styles.wrapper}>
                            <PreHeader />
                            <div className={styles.content}>
                                <Header />
                                <FullScreenLoading />
                            </div>
                        </div>
                    </PlaybookContext.Provider>
                </SessionContext.Provider>
            )
        }

        return (
            <SessionContext.Provider value={session}>
                <PlaybookContext.Provider value={playbook}>
                    <Head>
                        {generateNextSeo({
                            title: 'Step EarlyPay | Step',
                            description: 'Borrow up to $250 with Step EarlyPay',
                            openGraph: {
                                title: 'Step EarlyPay | Step',
                                description:
                                    'Borrow up to $250 with Step EarlyPay',
                            },
                            noindex: true,
                            nofollow: true,
                        })}
                    </Head>
                    <div className={styles.wrapper}>
                        <PreHeader />
                        <div className={styles.content}>
                            <Header />
                            {children}
                        </div>
                    </div>
                </PlaybookContext.Provider>
            </SessionContext.Provider>
        )
    },
)

type Props = {
    children: ReactNode
}
